import React, { useEffect, useState } from 'react';
import { useMobileAppDetection } from '../hooks/useMobileAppDetection';
import { mobileAppRedirectService } from '../services/mobileAppRedirect.service';
import { useNavigate, useLocation } from 'react-router-dom';

// Auth flows that must remain usable on mobile (e.g. a user tapping a
// "reset password" or "verify email" link from their phone's inbox)
// should never be bounced to the "view on desktop" page.
const MOBILE_REDIRECT_EXEMPT_PATHS = [
    '/forgot-password',
    '/reset-password',
    '/auth/action',
];

const isExemptFromMobileRedirect = (pathname: string) =>
    MOBILE_REDIRECT_EXEMPT_PATHS.some(
        (path) => pathname === path || pathname.startsWith(`${path}/`),
    );

interface MobileAppDetectorProps {
    children: React.ReactNode;
    autoRedirect?: boolean; // Whether to automatically redirect on mobile
    redirectDelay?: number; // Delay before redirect (default: 1000ms)
    showBanner?: boolean; // Whether to show mobile app banner
    bannerDelay?: number; // Delay before showing banner (default: 3000ms)
}

const MobileAppDetector: React.FC<MobileAppDetectorProps> = ({
    children,
    autoRedirect = false,
    redirectDelay = 1000,
}) => {
    const { isMobile, isIOS, isAndroid, canOpenApp } = useMobileAppDetection();
    const [hasAttemptedRedirect, setHasAttemptedRedirect] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Only run on mobile devices that can potentially open the app
        if (!isMobile || !canOpenApp || hasAttemptedRedirect) {
            return;
        }

        // Never block auth flows (forgot/reset password, email verification)
        // behind the "view on desktop" page - users need these to work from
        // whatever device they opened the link on.
        if (isExemptFromMobileRedirect(location.pathname)) {
            return;
        }

        // Check if user has opted out of auto-redirect
        const optedOut = localStorage.getItem('idearify-auto-redirect-disabled');
        if (optedOut) {
            return;
        }

        // Check if this is the first visit
        const hasVisited = localStorage.getItem('idearify-web-visited');
        if (!hasVisited) {
            localStorage.setItem('idearify-web-visited', 'true');
            
            // On first visit, attempt auto-redirect if enabled
            if (autoRedirect) {
                const timer = setTimeout(async () => {
                    try {
                        await mobileAppRedirectService.redirectToMobileApp(
                            isIOS,
                            isAndroid,
                            {
                                fallbackDelay: 2000,
                                showFallback: true,
                                onAppOpen: () => {
                                    console.log('Mobile app opened successfully');
                                },
                                onFallback: () => {
                                    console.log('Fallback to app store');
                                },
                            }
                        );
                    } catch (error) {
                        console.error('Mobile app redirect failed:', error);
                    }
                }, redirectDelay);

                setHasAttemptedRedirect(true);
                return () => clearTimeout(timer);
            }
        }
        navigate('/mobile-layout');
    }, [isMobile, canOpenApp, isIOS, isAndroid, autoRedirect, redirectDelay, hasAttemptedRedirect, location.pathname, navigate]);

    // Add mobile-specific meta tags
    useEffect(() => {
        if (isMobile) {
            // Add mobile app meta tags
            const addMetaTag = (name: string, content: string) => {
                let meta = document.querySelector(`meta[name="${name}"]`);
                if (!meta) {
                    meta = document.createElement('meta');
                    meta.setAttribute('name', name);
                    document.head.appendChild(meta);
                }
                meta.setAttribute('content', content);
            };

            // iOS specific meta tags
            if (isIOS) {
                addMetaTag('apple-itunes-app', 'app-id=123456789'); // Replace with actual app ID
                addMetaTag('apple-mobile-web-app-capable', 'yes');
                addMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
            }

            // Android specific meta tags
            if (isAndroid) {
                addMetaTag('mobile-web-app-capable', 'yes');
            }

            // Universal app meta tags
            addMetaTag('application-name', 'Idearify');
            addMetaTag('mobile-web-app-capable', 'yes');
        }
    }, [isMobile, isIOS, isAndroid]);

    return (
        <>
            {children}
            {/* Mobile app banner will be rendered by the Layout component */}
        </>
    );
};

export default MobileAppDetector;
